import React, { useState, useRef, useEffect } from 'react';
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward } from "lucide-react";
import { toast } from 'sonner';
import { ContextualNotificationPrompt } from "@/components/notifications/ContextualNotificationPrompt";
import WhyThisMattersCard from "@/components/education/WhyThisMattersCard";
import { supabase } from "@/integrations/supabase/client";

// Converts a stored public storage URL to a signed URL when the bucket is private.
// Returns the original URL on any failure.
async function resolveMediaUrl(url?: string): Promise<string | undefined> {
  if (!url) return url;
  const match = url.match(/\/storage\/v1\/object\/(?:public|sign)\/([^/]+)\/(.+?)(?:\?.*)?$/);
  if (!match) return url;
  const [, bucket, path] = match;
  try {
    const { data, error } = await supabase.storage.from(bucket).createSignedUrl(decodeURIComponent(path), 60 * 60 * 4);
    if (error || !data?.signedUrl) return url;
    return data.signedUrl;
  } catch {
    return url;
  }
}

interface MeditationPlayerProps {
  title: string;
  audioUrl?: string;
  videoUrl?: string;
  onClose: () => void;
}

const MeditationPlayer: React.FC<MeditationPlayerProps> = ({
  title,
  audioUrl,
  videoUrl,
  onClose
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  
  const [resolvedAudioUrl, setResolvedAudioUrl] = useState<string | undefined>(undefined);
  const [resolvedVideoUrl, setResolvedVideoUrl] = useState<string | undefined>(undefined);

  const audioRef = useRef<HTMLAudioElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  const currentMediaRef = showVideo && resolvedVideoUrl ? videoRef : audioRef;
  const currentUrl = showVideo && resolvedVideoUrl ? resolvedVideoUrl : resolvedAudioUrl;

  // Resolve signed URLs (handles private storage buckets)
  useEffect(() => {
    let cancelled = false;
    resolveMediaUrl(audioUrl).then((u) => { if (!cancelled) setResolvedAudioUrl(u); });
    resolveMediaUrl(videoUrl).then((u) => { if (!cancelled) setResolvedVideoUrl(u); });
    return () => { cancelled = true; };
  }, [audioUrl, videoUrl]);

  useEffect(() => {
    const media = currentMediaRef.current;
    if (!media) return;

    const updateTime = () => setCurrentTime(media.currentTime);
    const updateDuration = () => setDuration(media.duration);
    const handleEnded = () => setIsPlaying(false);

    media.addEventListener('timeupdate', updateTime);
    media.addEventListener('loadedmetadata', updateDuration);
    media.addEventListener('ended', handleEnded);
    media.addEventListener('error', (e) => {
      console.error('Media error:', e, media.error);
      toast.error('Failed to load media file. Please check the file URL.');
    });

    media.load();

    return () => {
      media.removeEventListener('timeupdate', updateTime);
      media.removeEventListener('loadedmetadata', updateDuration);
      media.removeEventListener('ended', handleEnded);
    };
  }, [currentMediaRef, showVideo, currentUrl]);

  const togglePlayPause = async () => {
    const media = currentMediaRef.current;
    if (!media) return;

    try {
      if (isPlaying) {
        media.pause();
        setIsPlaying(false);
      } else {
        await media.play();
        setIsPlaying(true);
      }
    } catch (error) {
      console.error('Playback error:', error);
      toast.error('Unable to play media. Please check the file format.');
      setIsPlaying(false);
    }
  };

  const handleSeek = (value: number[]) => {
    const media = currentMediaRef.current;
    if (!media) return;
    
    const newTime = value[0];
    media.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (value: number[]) => {
    const newVolume = value[0];
    setVolume(newVolume);
    
    const media = currentMediaRef.current;
    if (media) {
      media.volume = newVolume;
    }
  };

  const toggleMute = () => {
    const media = currentMediaRef.current;
    if (!media) return;
    
    setIsMuted(!isMuted);
    media.muted = !isMuted;
  };

  const skipForward = () => {
    const media = currentMediaRef.current;
    if (!media) return;
    
    media.currentTime = Math.min(media.currentTime + 15, duration);
  };

  const skipBackward = () => {
    const media = currentMediaRef.current;
    if (!media) return;
    
    media.currentTime = Math.max(media.currentTime - 15, 0);
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-semibold text-foreground">{title}</h2>
          <Button variant="ghost" onClick={onClose}>×</Button>
        </div>

        {/* Media Toggle (if both audio and video available) */}
        {audioUrl && videoUrl && (
          <div className="flex justify-center">
            <div className="flex bg-muted rounded-lg p-1">
              <Button
                variant={!showVideo ? "default" : "ghost"}
                size="sm"
                onClick={() => setShowVideo(false)}
              >
                Audio Only
              </Button>
              <Button
                variant={showVideo ? "default" : "ghost"}
                size="sm"
                onClick={() => setShowVideo(true)}
              >
                With Video
              </Button>
            </div>
          </div>
        )}

        {/* Video Player */}
        {showVideo && resolvedVideoUrl && (
          <div className="relative aspect-video bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              src={resolvedVideoUrl}
              className="w-full h-full object-cover"
              controls={false}
            />
          </div>
        )}

        {/* Audio Element (hidden) */}
        {resolvedAudioUrl && (
          <audio
            ref={audioRef}
            src={resolvedAudioUrl}
            preload="metadata"
            style={{ display: 'none' }}
          />
        )}

        {/* Progress Bar */}
        <div className="space-y-2">
          <Slider
            value={[currentTime]}
            max={duration || 100}
            step={1}
            onValueChange={handleSeek}
            className="w-full"
          />
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{formatTime(currentTime)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        {/* Controls */}
        <div className="flex items-center justify-center space-x-4">
          <Button variant="ghost" size="sm" onClick={skipBackward}>
            <SkipBack className="h-5 w-5" />
          </Button>
          
          <Button size="lg" onClick={togglePlayPause} className="rounded-full w-12 h-12">
            {isPlaying ? <Pause className="h-6 w-6" /> : <Play className="h-6 w-6" />}
          </Button>
          
          <Button variant="ghost" size="sm" onClick={skipForward}>
            <SkipForward className="h-5 w-5" />
          </Button>
        </div>

        {/* Volume Control */}
        <div className="flex items-center space-x-3">
          <Button variant="ghost" size="sm" onClick={toggleMute}>
            {isMuted || volume === 0 ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </Button>
          
          <Slider
            value={[isMuted ? 0 : volume]}
            max={1}
            step={0.1}
            onValueChange={handleVolumeChange}
            className="flex-1 max-w-32"
          />
        </div>

        {/* Education Card */}
        <WhyThisMattersCard
          toolName="Meditation"
          shortDescription="Meditation physically changes your brain structure for reduced stress."
          whyItMatters="Regular meditation increases grey matter in areas associated with emotional regulation, learning, and memory. It also thickens the prefrontal cortex, improving decision-making and reducing reactivity to stress."
          researchSummary="MRI studies show that 8 weeks of meditation practice produces measurable increases in brain density. Meditators show 50% less activity in the amygdala (fear centre) when presented with stressful images."
          howToOptimise="Consistency matters more than duration. Start with 5 minutes daily and gradually increase. Morning meditation sets a calm tone for the day; evening helps process experiences and improve sleep."
          tags={['Brain Health', 'Stress Reduction', 'Neuroplasticity']}
          researchLinks={[
            { title: 'Meditation & Brain Structure - Harvard Study', url: 'https://pubmed.ncbi.nlm.nih.gov/21071182/' }
          ]}
        />

        {/* Contextual notification prompt after meditation */}
        <ContextualNotificationPrompt context="meditation" />
      </Card>
    </div>
  );
};

export default MeditationPlayer;